import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Link2, Code2, Check, Loader2, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export function EmbedDialog({ open, onOpenChange, resourceType, resourceId, resourceName }) {
  const [generating, setGenerating] = useState(false);
  const [embedResult, setEmbedResult] = useState(null);
  const [expiresHours, setExpiresHours] = useState('168');
  const [label, setLabel] = useState('');
  const [copied, setCopied] = useState(null);

  const baseUrl = window.location.origin;

  const generateEmbed = async () => {
    setGenerating(true);
    try {
      const result = await api.generateEmbed({
        resource_type: resourceType,
        resource_id: resourceId,
        expires_hours: Number(expiresHours),
        label: label || `${resourceName} embed`,
      });
      setEmbedResult(result);
      toast.success('Embed link generated');
    } catch (err) { toast.error(err.message); }
    finally { setGenerating(false); }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copied to clipboard');
  };

  const embedUrl = embedResult ? `${baseUrl}${embedResult.embed_url}` : '';
  const iframeCode = embedResult ? `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" style="border-radius:12px;border:1px solid #1e293b;" allowfullscreen></iframe>` : '';

  return (
    <Dialog open={open} onOpenChange={v => { onOpenChange(v); if (!v) { setEmbedResult(null); setLabel(''); } }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            Share & Embed
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-3 rounded-xl bg-muted/20 border border-border/30">
            <p className="text-xs text-muted-foreground">Sharing</p>
            <p className="text-sm font-heading font-semibold text-foreground">{resourceName}</p>
            <Badge className="text-[8px] mt-1 bg-primary/10 text-primary border-primary/20">{resourceType}</Badge>
          </div>

          {!embedResult ? (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] text-muted-foreground">Label (optional)</Label>
                <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Client portal embed" className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Link Expires</Label>
                <Select value={expiresHours} onValueChange={setExpiresHours}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                    <SelectItem value="720">30 days</SelectItem>
                    <SelectItem value="8760">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generateEmbed} disabled={generating} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="generate-embed-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Generate Shareable Link
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Direct Link */}
              <div>
                <Label className="text-[10px] text-muted-foreground mb-1 block">Shareable URL</Label>
                <div className="flex gap-1.5">
                  <Input value={embedUrl} readOnly className="rounded-lg bg-muted/30 border-border/30 text-[11px] font-mono flex-1 min-w-0 truncate" data-testid="embed-url-input" />
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(embedUrl, 'url')} className="rounded-lg w-9 h-9 flex-shrink-0" data-testid="copy-url-btn">
                    {copied === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>

              {/* iFrame Code */}
              <div>
                <Label className="text-[10px] text-muted-foreground mb-1 block">Embed Code (iFrame)</Label>
                <div className="flex gap-1.5">
                  <div className="flex-1 min-w-0 rounded-lg bg-muted/30 border border-border/30 p-2 text-[10px] font-mono text-muted-foreground break-all overflow-hidden max-h-20 overflow-y-auto" data-testid="iframe-code">
                    {iframeCode}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(iframeCode, 'iframe')} className="rounded-lg w-9 h-9 flex-shrink-0" data-testid="copy-iframe-btn">
                    {copied === 'iframe' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Code2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>

              <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10 text-[10px] text-emerald-400">
                Expires: {new Date(embedResult.expires_at).toLocaleString()}
              </div>

              <Button variant="outline" onClick={() => setEmbedResult(null)} className="w-full rounded-lg text-xs">
                Generate Another Link
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
